import { useEffect, useState } from "react";
import { useVenue } from "../lib/venue";
import { joinMailingList } from "../lib/api";
import { pushCtaClick, pushFormInteraction } from "../lib/analytics";
import { isNativeApp } from "../lib/platform";

// Dismissible email-capture pop-up for the record-shop WEBSITE only. NEVER
// rendered in the native app (store-guideline risk), the guard is belt-and-
// braces since the caller (record web-shell) is web-only too. Uses localStorage
// so it never reappears after a dismiss or a successful sign-up, and waits a
// few seconds before the first show so it doesn't slam a fresh visitor.
const DELAY_MS = 6000;

function storageKey(venueId: string): string {
  return `assembly:mailing-popup:${venueId}`;
}

export function MailingListPopup() {
  const { data } = useVenue();
  const venueId = data?.venue.id;
  const isRecordShop = data?.venue.archetype === "RECORD_SHOP";
  const native = isNativeApp();

  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (native || !isRecordShop || !venueId) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(storageKey(venueId)) != null;
    } catch {
      /* storage disabled, just show it */
    }
    if (dismissed) return;
    const t = window.setTimeout(() => setVisible(true), DELAY_MS);
    return () => window.clearTimeout(t);
  }, [native, isRecordShop, venueId]);

  function persistDismissed() {
    if (!venueId) return;
    try {
      localStorage.setItem(storageKey(venueId), new Date().toISOString());
    } catch {
      /* ignore */
    }
  }

  function close() {
    persistDismissed();
    setVisible(false);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!venueId || !email.trim()) return;
    pushCtaClick("subscribe");
    // noValidate on the form means a malformed address reaches us instead of
    // being silently blocked by the browser's own type="email" validation,
    // otherwise this check (and the fail event below) would never run.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      pushFormInteraction("fail");
      setStatus("error");
      setError("Enter a valid email address");
      return;
    }
    setStatus("saving");
    setError("");
    try {
      await joinMailingList(venueId, email.trim(), "popup");
      pushFormInteraction("success");
      setStatus("done");
      persistDismissed();
      window.setTimeout(() => setVisible(false), 2200);
    } catch (err) {
      pushFormInteraction("fail");
      setStatus("error");
      setError((err as Error).message || "Something went wrong");
    }
  }

  if (native || !isRecordShop || !visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-sm sm:left-auto sm:right-6">
      <div className="relative overflow-hidden rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] p-5 shadow-2xl">
        <button
          type="button"
          onClick={close}
          aria-label="Dismiss"
          className="border-0 bg-transparent absolute right-3 top-3 text-[var(--muted)] transition hover:text-[var(--foreground)]"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
            <path strokeLinecap="round" d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>

        {status === "done" ? (
          <div className="py-2 text-center">
            <p className="text-sm font-bold text-[var(--foreground)]">You're on the list 🎉</p>
            <p className="mt-1 text-xs text-[var(--subtle)]">Watch your inbox for your 10% code.</p>
          </div>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--accent)]">Join the list</p>
            <h3 className="mt-1 text-lg font-black leading-tight text-[var(--foreground)]">
              10% off your first order
            </h3>
            <p className="mt-1 text-xs text-[var(--subtle)]">
              New arrivals, staff picks &amp; members-only drops, straight to your inbox.
            </p>
            <form onSubmit={submit} noValidate className="mt-3 flex flex-col gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] placeholder:text-[var(--muted)] focus:border-[var(--accent)] focus:outline-none"
              />
              <button
                type="submit"
                disabled={status === "saving"}
                className="rounded-[var(--radius)] bg-[var(--accent)] px-4 py-2 text-sm font-bold transition hover:opacity-90 disabled:opacity-50"
                style={{ color: "var(--accent-fg)" }}
              >
                {status === "saving" ? "Joining…" : "Get my 10% off"}
              </button>
              {status === "error" && <p className="text-xs text-red-400">{error}</p>}
            </form>
            <button type="button" onClick={close} className="border-0 bg-transparent mt-2 text-[11px] text-[var(--muted)] underline underline-offset-2 hover:text-[var(--foreground)]">
              No thanks
            </button>
          </>
        )}
      </div>
    </div>
  );
}
